import React, { useEffect, useState, useRef } from 'react';
import Footer from '../components/Footer';
import Header from '../components/Header';
import Select from 'react-select';
import Swal from 'sweetalert2';
import { generateAxiosInstance } from '../hooks/useAuthorizedRequest';
import { generateAxiosInstanceCommonFileUpload } from '../hooks/useAuthorizedRequestForFileUpload';
import { useNavigate } from 'react-router-dom';
import { Loader } from '../components/ModalsCommonFunctions';
import { findIndexById, findPageNumber, deleteItem, SortIcon, searchArray } from '../components/Navigation';
import Pagination from '../components/Pagination';

const CreateManagementUser = () => {
    const authorizedAxios = generateAxiosInstance()
    const authorizedAxiosCommonFileUpload = generateAxiosInstanceCommonFileUpload()
    const initialFormState = {
        email: "",
        personName: "",
        contactNumber: "",
        gender: "",
        address: "",
        district: "",
        state: "",
        pincode: "",
        dob: "",
        bloodGroup: "",
        logoName: "",
        logoPath: "",
        instituteMasterIds: [],
        password: ""
    }
    const [createUser, setCreateUser] = useState(initialFormState)
    const [userList, setUserList] = useState([])
    const [editId, setEditId] = useState(null)
    const [showPassword, setShowPassword] = useState([]);
    const [refreshList, setRefreshList] = useState(false);
    const [instituteList, setInstituteList] = useState([]);
    const [stateList, setStateList] = useState([]);
    const [distList, setDistList] = useState([]);
    const logoRef = useRef(null);

    const [startIndex, setStartIndex] = useState(null);
    const [currPage, setCurrPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [displayedData, setDisplayedData] = useState([]);
    const [totalPages, setTotalPages] = useState(0);
    const [saveId, setSaveId] = useState(null);
    const [addNew, setAddNew] = useState(false);

    const [userIdSort, setUserIdSort] = useState(null);
    const [emailSort, setEmailSort] = useState(null);

    const [searchValue, setSearchValue] = useState(null);
    const searchKeys = ["userDetails.userUniqueId", "email", "contactNumber", "instituteManagementUserInstituteMapping.instituteMasterDetails.instituteId", "instituteManagementUserInstituteMapping.instituteMasterDetails.instituteName"];


    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleNavigate = (route) => {
        navigate(route);
    };

    useEffect(() => {
        getInstituteList();
        getStateList();
    }, [refreshList]);

    useEffect(() => {
        getUserList();
    }, [createUser.instituteMasterIds]);

    const getApiUrl = () => {
        let apiUrl = `/get-institute-management-users?`;
        if (createUser.instituteMasterIds.length !== 0) {
            apiUrl += `instituteMasterIds=${createUser.instituteMasterIds}&`;
        }

        return apiUrl;
    }

    const getUserList = async () => {
        try {
            const response = await authorizedAxios.get(getApiUrl());
            setUserList(response.data.data);
        } catch (error) {
            setUserList([]);
            const errorMsg = error?.response?.data?.message || 'Something went wrong!';
            console.log(errorMsg);
        }
    }

    useEffect(() => {
        if (searchValue === '' && searchValue !== null) {
            setCurrPage(1);
            getUserList();
        }
    }, [searchValue]);

    const handleEmptySearch = (e) => {
        setSearchValue(e.target.value);
    }

    const handleSearch = async () => {
        if (searchValue !== null) {
            setUserList(await searchArray("A", getApiUrl(), searchKeys, searchValue.trim(), "data"))
        }
    }

    useEffect(() => {
        if (saveId) {
            const pageNumber = findPageNumber(userList, saveId, itemsPerPage);
            setCurrPage(pageNumber);
        }
    }, [saveId, addNew])

    useEffect(() => {
        setTotalPages(Math.ceil(userList.length / itemsPerPage));
        const startIndex = (currPage - 1) * itemsPerPage;
        let endIndex = startIndex + itemsPerPage;

        if (endIndex > userList.length) {
            endIndex = userList.length;
        }
        setStartIndex(startIndex);
        setDisplayedData(userList.slice(startIndex, endIndex));
    }, [userList, currPage, itemsPerPage]);

    const getStateList = () => {
        const apiUrlForInstitute = `/all-states`;
        authorizedAxios && authorizedAxios.get(apiUrlForInstitute)
            .then(response => {
                setStateList(response.data.result.map((state) => {
                    return {
                        value: state.id,
                        label: state.stateName
                    }
                }))
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                Swal.fire({
                    text: errorMsg
                })
            });
    }

    const getInstituteList = () => {
        const apiUrlForInstitute = `/get-institute-master`;
        authorizedAxios && authorizedAxios.get(apiUrlForInstitute)
            .then(response => {
                setInstituteList(response.data.result.map((institute) => {
                    return {
                        value: institute.id,
                        label: institute.instituteName
                    }
                }))
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                console.error('Error fetching data:', errorMsg);
            });
    }

    const handleChangeState = (selectedOptions) => {
        setCreateUser((prevData) => {
            return {
                ...prevData,
                state: selectedOptions?.label,
            }
        })

        const apiUrlForInstitute = `/state-wise-district-city?id=${selectedOptions?.value}`;
        authorizedAxios && authorizedAxios.get(apiUrlForInstitute)
            .then(response => {
                setDistList(response.data.districts.map((board) => {
                    return {
                        value: board.id,
                        label: board.districtName
                    }
                }))
            })
            .catch(error => {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                Swal.fire({
                    text: errorMsg
                })
            });
    }

    const handleUploadLogo = async (e) => {
        setLoading(true);
        const file = e.target.files[0];
        const body = new FormData();
        body.append("file", file);
        const apiUrl = "uploadimage";
        authorizedAxiosCommonFileUpload && authorizedAxiosCommonFileUpload.post(apiUrl, body)
            .then((res) => {
                setCreateUser((prevData) => {
                    if (e.target.name === 'logoName' && file) {
                        return {
                            ...prevData,
                            logoPath: res.data.url,
                            logoName: file.name,
                        }
                    }
                })
            })
            .catch((err) => {
                const errMsg = err.response.data.error || "Something went wrong!"
                Swal.fire({
                    text: errMsg
                })
            })
            .finally(() => {
                setLoading(false);
            })
    }

    const handleChangeDist = (selectedOptions) => {
        setCreateUser((prevData) => {
            return {
                ...prevData,
                district: selectedOptions.label,
            }
        })
    }

    const handleInstituteChange = (selectedOptions) => {
        const selectedIds = selectedOptions?.map((option) => option.value.toString());
        setCreateUser((prevData) => {
            return {
                ...prevData,
                instituteMasterIds: selectedIds,
            }
        })
    }

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setCreateUser((prevData) => {
            if (name == 'contactNumber' && value.length > 10) {
                return {
                    ...prevData,
                }
            } else {
                return {
                    ...prevData,
                    [e.target.name]: e.target.value,
                }
            }
        })
    }

    //error
    const initialErrorState = {

    }

    const [error, setError] = useState(initialErrorState)

    const validateForm = () => {
        const mandatoryFields = Object.keys(createUser).filter(field => !['personName', 'gender', 'address', 'district', 'state', 'pincode', 'dob', 'logoName', 'logoPath', 'bloodGroup'].includes(field));
        const contactNumberValid = createUser.contactNumber.length == 10;
        return Object.values(error).every((error) => error === '') &&
            mandatoryFields.every((field) => typeof createUser[field] === "string" ? createUser[field] !== '' : createUser[field]?.length !== 0) && contactNumberValid;
    };

    useEffect(() => {
        if (!Object.values(error).every((error) => error === '')) {
            for (const [key, value] of Object.entries(createUser)) {
                if (key !== 'personName' && key !== 'gender' && key !== 'address' && key !== 'district' && key !== 'state' && key !== 'pincode' && key !== 'dob' && key !== 'logoName' && key !== 'logoPath' && key !== 'bloodGroup') {
                    if (key == 'contactNumber' && value.length > 0 && value.length < 10) {
                        setError((prevData) => ({
                            ...prevData,
                            contactNumber: 'Contact number must be at least 10 characters long.',
                        }));
                    } else if (value === '' || value.length === 0) {
                        setError((prevData) => ({
                            ...prevData,
                            [key]: 'This field is required.',
                        }));
                    } else {
                        setError((prevData) => ({
                            ...prevData,
                            [key]: '',
                        }));
                    }
                }
            }
        }
    }, [createUser]);

    const createNewUser = async () => {
        if (validateForm()) {
            try {
                setLoading(true);
                const apiUrl = `/add-institute-management-users`;
                const response = await authorizedAxios.post(apiUrl, createUser);
                await getUserList();
                setCreateUser(initialFormState);
                setError(initialErrorState);
                logoRef.current.value = '';

                setSaveId(response.data.createdInstituteUser.id);
                setUserIdSort(null);
                setEmailSort(null);

                Swal.fire(
                    { text: response.data.message }
                );
            } catch (error) {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                Swal.fire(
                    { text: errorMsg }
                );
            } finally {
                setLoading(false);
            }
        } else {
            for (const [key, value] of Object.entries(createUser)) {
                if (key !== 'personName' && key !== 'gender' && key !== 'address' && key !== 'district' && key !== 'state' && key !== 'pincode' && key !== 'dob' && key !== 'logoName' && key !== 'logoPath' && key !== 'bloodGroup') {
                    if (key == 'contactNumber' && value.length > 0 && value.length < 10) {
                        setError((prevData) => ({
                            ...prevData,
                            contactNumber: 'Contact number must be at least 10 characters long.',
                        }));
                    } else if (createUser[key] == '' || value.length === 0) {
                        setError((prevData) => {
                            return {
                                ...prevData,
                                [key]: 'This field is required.',
                            }
                        })
                    } else {
                        setError((prevData) => {
                            return {
                                ...prevData,
                                [key]: '',
                            }
                        })
                    }
                }
            }
        }
    }

    const updateUser = async () => {
        if (validateForm()) {
            try {
                setLoading(true);
                const apiUrl = `/edit-institute-management-users?id=${editId}`;
                const response = await authorizedAxios.put(apiUrl, createUser);
                await getUserList();
                setCreateUser(initialFormState);
                setError(initialErrorState);
                logoRef.current.value = '';
                setEditId(null);
                // setSaveId(response.data.data.id);

                Swal.fire(
                    { text: response?.data?.message }
                );
            } catch (error) {
                const errorMsg = error?.data?.message || 'Something went wrong!';
                Swal.fire(
                    { text: errorMsg }
                );
            } finally {
                setLoading(false);
            }
        } else {
            for (const [key, value] of Object.entries(createUser)) {
                if (key !== 'personName' && key !== 'gender' && key !== 'address' && key !== 'district' && key !== 'state' && key !== 'pincode' && key !== 'dob' && key !== 'logoName' && key !== 'logoPath' && key !== 'bloodGroup') {
                    if (key == 'contactNumber' && value.length > 0 && value.length < 10) {
                        setError((prevData) => ({
                            ...prevData,
                            contactNumber: 'Contact number must be at least 10 characters long.',
                        }));
                    } else if (createUser[key] == '' || value.length === 0) {
                        setError((prevData) => {
                            return {
                                ...prevData,
                                [key]: 'This field is required.',
                            }
                        })
                    } else {
                        setError((prevData) => {
                            return {
                                ...prevData,
                                [key]: '',
                            }
                        })
                    }
                }
            }
        }
    }

    const togglePasswordVisibility = (index) => {
        setShowPassword(prevState => {
            const updatedShowPassword = [...prevState];
            updatedShowPassword[index] = !updatedShowPassword[index];
            return updatedShowPassword;
        });
    };

    const GetMappedData = ({ data }) => {
        return (
            <td>
                {data.map((details, index) => {
                    const instituteId = details.instituteMasterDetails.instituteId;
                    const instituteName = details.instituteMasterDetails.instituteName;
                    return (
                        <div key={index}>
                            {instituteId} &rarr; {instituteName}
                        </div>
                    );
                })}
            </td>
        );
    };

    const changeActive = async (id, isActive) => {
        try {
            setLoading(true);
            const apiUrl = `/toggle-institute-management-users?user_id=${id}&is_active=${isActive}`;
            const response = await authorizedAxios.put(apiUrl);
            await getUserList();
            setRefreshList(!refreshList)
            setTimeout(() => {
                Swal.fire({ text: response.data.message });
            }, 100);
        } catch (error) {
            const errorMsg = error?.response?.data?.message || 'Something went wrong!';
            Swal.fire(
                { text: errorMsg }
            );
        } finally {
            setLoading(true);
        }
    }

    const sendMail = async (email, userId, password) => {
        setLoading(true);
        const data = {
            email: email,
            userId: userId,
            password: password
        };

        try {
            const apiUrl = `/mail-institute-management-users`;
            const response = await authorizedAxios.post(apiUrl, data);
            setTimeout(() => {
                Swal.fire({ text: response.data.message });
            }, 100);
        } catch (error) {
            const errorMsg = error?.response?.data?.message || 'Something went wrong!';
            Swal.fire(
                { text: errorMsg }
            );
        } finally {
            setLoading(false);
        }
    }

    const deleteUser = async (id) => {
        let confirmed = window.confirm('Are you sure? you want to delete this record.');
        if (confirmed) {
            try {
                setLoading(true);
                const apiUrl = `/delete-institute-management-users?id=${id}`;
                const response = await authorizedAxios.delete(apiUrl);
                const page = deleteItem(currPage, itemsPerPage, userList.length);
                // setRefresh(!refresh);
                await getUserList();
                setCurrPage(page);

                Swal.fire(
                    { text: response.data.message }
                );
            } catch (error) {
                const errorMsg = error?.response?.data?.message || 'Something went wrong!';
                Swal.fire(
                    { text: errorMsg }
                );
            } finally {
                setLoading(false);
            }
        }
    }

    const handleInputReset = () => {
        setEditId('');
        setCreateUser(initialFormState);
        setError(initialErrorState);
        logoRef.current.value = '';
    }

    useEffect(() => {
        if (userIdSort == true || userIdSort == false) {
            setEmailSort(null);
        }
    }, [userIdSort])

    useEffect(() => {
        if (emailSort == true || emailSort == false) {
            setUserIdSort(null);
        }
    }, [emailSort])

    return (
        <>
            <Loader loading={loading} />
            <div className="wrapper d-flex flex-column min-vh-100 bg-light">
                <div id='scroll-up'></div>
                <Header />
                <div className="container-fluid">
                    <nav aria-label="breadcrumb">
                        <ol className="breadcrumb my-0 ms-2">
                            <li className="breadcrumb-item dashboard" onClick={() => handleNavigate('/dashboard')}>
                                <span>Dashboard</span>
                            </li>
                            <li className="breadcrumb-item active"><span>Create Management User </span></li>
                        </ol>
                    </nav>
                </div>
                <div id='scroll-up'></div>
                <div className="container-lg">
                    <div className="row">
                        <div className="card border-info mx-auto mt-3">
                            <div className="card-content collpase show">
                                <div className="card-header" style={{ backgroundColor: '#3081D0', borderRadius: 0 }}>
                                    <h4 className="text-white"><b><span className="fa fa-file-o"></span><i class="fa-solid fa-user"></i> Create Management User</b></h4>
                                </div>
                                <div className="card-body ">
                                    <div className="row">
                                        <div className="col-md-12">
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <label for="personName" className="col-form-label">Name :</label>
                                                    <input type="text" className="form-control" name="personName" value={createUser.personName} onChange={handleInputChange} />
                                                </div>
                                                <div className="col-md-6 view_content_radio_btn">
                                                    <label for="inputPassword" className="col-form-label">Gender :</label>
                                                    <label>
                                                        <input type="radio" name="gender" value="M" checked={createUser.gender == "M"} onChange={handleInputChange} /> &nbsp;Male
                                                    </label>
                                                    <label>
                                                        <input type="radio" name="gender" value="F" checked={createUser.gender == "F"} onChange={handleInputChange} /> &nbsp;Female
                                                    </label>
                                                    <label>
                                                        <input type="radio" name="gender" value="O" checked={createUser.gender == "O"} onChange={handleInputChange} /> &nbsp;Other
                                                    </label>
                                                </div>

                                                <div className="col-md-6">
                                                    <label for="address" className="col-form-label">Address :</label>
                                                    <input type="text" className="form-control" name="address" value={createUser.address} onChange={handleInputChange} />
                                                    {error.address && <p style={{ color: 'red' }}>{error.address}</p>}
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="state" className="col-form-label">State :</label>
                                                    <Select
                                                        value={createUser.state == '' ? null : stateList.filter(option =>
                                                            createUser.state && createUser.state == option.label && (option.value)
                                                        )[0]}
                                                        name="state"
                                                        options={stateList}
                                                        className="basic-multi-select"
                                                        classNamePrefix="Select Board"
                                                        onChange={handleChangeState}
                                                    />
                                                    {/* {error.state && <p style={{ color: 'red' }}>{error.state}</p>} */}
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="district" className="col-form-label">District :</label>
                                                    <Select
                                                        value={createUser.district == '' ? null : distList.filter(option =>
                                                            createUser.district && createUser.district == option.label && (option.value)
                                                        )[0]}
                                                        name="district"
                                                        options={distList}
                                                        className="basic-multi-select"
                                                        classNamePrefix="Select Board"
                                                        onChange={handleChangeDist}
                                                    />
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="pincode" className="col-form-label">Pincode :</label>
                                                    <input type="number" className="form-control" name="pincode" value={createUser.pincode} onChange={handleInputChange} />
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="dob" className="col-form-label">Date of Birth :</label>
                                                    <input type="date" className="form-control" name="dob" value={createUser.dob} onChange={handleInputChange} />
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="bloodGroup" className="col-form-label">Blood Group :</label>
                                                    <input type="text" className="form-control" name="bloodGroup" value={createUser.bloodGroup} onChange={handleInputChange} />
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="email" className="col-form-label"><span style={{ color: "red" }}>*</span>Enter Email :</label>
                                                    <input required type="email" id='email' className="form-control" name="email" value={createUser.email} onChange={handleInputChange} />

                                                    {error.email && <p style={{ color: 'red' }}>{error.email}</p>}
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="password" className="col-form-label"><span style={{ color: "red" }}>*</span>Enter Password :</label>
                                                    <input required type="password" id='password' className="form-control" name="password" value={createUser.password} onChange={handleInputChange} />

                                                    {error.password && <p style={{ color: 'red' }}>{error.password}</p>}
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="contactNumber" className="col-form-label"><span style={{ color: "red" }}>*</span>Enter Contact Number :</label>
                                                    <input required type="number" id='contactNumber' className="form-control custom-input" name="contactNumber" value={createUser.contactNumber} onChange={handleInputChange} />
                                                    {error.contactNumber && <p style={{ color: 'red' }}>{error.contactNumber}</p>}
                                                </div>

                                                <div className="col-md-6">
                                                    <label for="logoName" className="col-form-label">Upload Institute logo :</label>
                                                    <input type="file" className="form-control" name="logoName" onChange={handleUploadLogo} ref={logoRef} />
                                                </div>
                                                <div className="col-md-6">
                                                    <label for="instituteMasterIds" className="col-form-label"><span style={{ color: "red" }}>*</span>Select Institute :</label>
                                                    <Select
                                                        value={instituteList.filter(option =>
                                                            createUser.instituteMasterIds && createUser.instituteMasterIds.includes(option.value.toString())
                                                        )}
                                                        isMulti
                                                        name="instituteMasterIds"
                                                        options={instituteList}
                                                        className="basic-multi-select"
                                                        classNamePrefix="Select Institute"
                                                        onChange={handleInstituteChange}
                                                    />
                                                    {error.instituteMasterIds && <p style={{ color: 'red' }}>{error.instituteMasterIds}</p>}
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-12 mt-3 mb-1" style={{ textAlign: 'center' }}>
                                                    <div className="col-auto">
                                                        {!editId &&
                                                            <button className="btn btn-primary" type="button" style={{ marginRight: '20px' }} onClick={createNewUser}>Save</button>
                                                        }
                                                        <button className="btn btn-outline-danger delete_icon" type="button" style={{ marginRight: '20px' }} onClick={handleInputReset}>Reset</button>
                                                        {
                                                            editId && (<button className="btn btn-success approve_icon" type="button" onClick={updateUser} disabled={!editId}>Update</button>)
                                                        }
                                                    </div>
                                                </div>

                                                <div className="d-grid gap-2 mt-2">
                                                    <div class="tab-content rounded-bottom custom_table custom_table_width">
                                                        <div className='search_btn_row'>
                                                            <input
                                                                className="form-control mr-sm-2"
                                                                type="Search"
                                                                placeholder="Search"
                                                                value={searchValue}
                                                                onChange={handleEmptySearch}
                                                                onKeyDown={(event) => {
                                                                    if (event.key === 'Enter') {
                                                                        handleSearch();
                                                                    }
                                                                }}
                                                            />
                                                            <button className="btn btn-outline-primary" onClick={handleSearch}><i class="fa fa-search" aria-hidden="true"></i></button>
                                                        </div>
                                                        <div class="tab-pane p-3 active preview" role="tabpanel" id="preview-1000">
                                                            <table class="table">
                                                                <thead>
                                                                    <tr>
                                                                        <th scope="col" className='sl_no'>Sr. No.</th>
                                                                        <th scope="col">
                                                                            User Id
                                                                            <SortIcon sortState={userIdSort} setSortState={setUserIdSort} setData={setUserList} setCurrPage={setCurrPage} sortingPath={"userDetails.userUniqueId"} />
                                                                        </th>
                                                                        <th scope="col">
                                                                            Email
                                                                            <SortIcon sortState={emailSort} setSortState={setEmailSort} setData={setUserList} setCurrPage={setCurrPage} sortingPath={"email"} />
                                                                        </th>
                                                                        <th scope="col">Password</th>
                                                                        <th scope="col">Contact Number</th>
                                                                        <th scope='col'>Mapped Institute</th>
                                                                        <th scope="col">Active/Inactive</th>
                                                                        <th scope="col">Send Mail</th>
                                                                        <th scope="col">Action</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {
                                                                        displayedData?.length > 0 ?
                                                                            displayedData.map((user, index) => {
                                                                                return (
                                                                                    <tr>
                                                                                        <th scope="row" className='sl_no'>{startIndex + index + 1}</th>
                                                                                        <td>{user?.userDetails.userUniqueId}</td>
                                                                                        <td>{user?.email}</td>
                                                                                        <td>{showPassword[index] ? (user?.userDetails.password) : ('••••••••')}
                                                                                            <button
                                                                                                className="btn btn-link"
                                                                                                type="button"
                                                                                                onClick={() => togglePasswordVisibility(index)}
                                                                                            >
                                                                                                <i className={`fa ${showPassword[index] ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                                                                            </button>
                                                                                        </td>
                                                                                        <td>{user?.contactNumber}</td>
                                                                                        <GetMappedData data={user.instituteManagementUserInstituteMapping} />
                                                                                        <td>
                                                                                            <div className={`toggle-container ${user.isActive ? 'active' : ''}`} onClick={() => changeActive(user.id, !user.isActive)}>
                                                                                                <div className="toggle-background"></div>
                                                                                                <div className="toggle-switch"></div>
                                                                                            </div>
                                                                                        </td>
                                                                                        <td>
                                                                                            <button className="btn btn-outline-info" type="button" onClick={() => sendMail(user.email, user.uniqueId, user.userDetails.password)}>
                                                                                                <i className="fa fa-paper-plane"></i>
                                                                                            </button>
                                                                                        </td>
                                                                                        <td>
                                                                                            <button className="btn btn-outline-warning edit_icon" type="button" onClick={() => {
                                                                                                setCreateUser({
                                                                                                    email: user.email,
                                                                                                    personName: user.personName,
                                                                                                    contactNumber: user.contactNumber,
                                                                                                    gender: user.gender,
                                                                                                    address: user.address,
                                                                                                    district: user.district,
                                                                                                    state: user.state,
                                                                                                    pincode: user.pincode,
                                                                                                    dob: user.dob,
                                                                                                    bloodGroup: user.bloodGroup,
                                                                                                    logoName: user.logoName,
                                                                                                    logoPath: user.logoPath,
                                                                                                    instituteMasterIds: user.instituteManagementUserInstituteMapping.map((institute) => (
                                                                                                        institute.instituteMasterDetails.id.toString()
                                                                                                    )),
                                                                                                    password: user.userDetails.password
                                                                                                });
                                                                                                setEditId(user.id);
                                                                                                document.getElementById('scroll-up').scrollIntoView({ behavior: 'smooth' });
                                                                                            }}><i class="fa-light fa-pen-to-square"></i></button>
                                                                                            <button className="btn btn-outline-danger delete_icon" type="button" disabled={editId === user.id} onClick={() => deleteUser(user.id)}> <i class="fa-light fa-trash-can"></i></button>
                                                                                        </td>
                                                                                    </tr>
                                                                                )
                                                                            })
                                                                            :
                                                                            (<tr>
                                                                                <th colspan={"100%"} style={{ textAlign: 'center', padding: "50px" }}>No user Structure Available</th>
                                                                            </tr>)
                                                                    }
                                                                </tbody>
                                                            </table>
                                                            <Pagination currPage={currPage} setCurrPage={setCurrPage} totalPages={totalPages} itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage} totalItem={userList.length} />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    )
};

export default CreateManagementUser;